import { Suspense, useState } from 'react';

import { createFileRoute } from '@tanstack/react-router';

import {
	faTriangleExclamation,
	faUserPlus,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	Backdrop,
	Box,
	Button,
	Container,
	LinearProgress,
	Paper,
	Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import { useUser } from '@repo/auth';
import { RightSlide } from '@repo/ui/RightSlide';

import { UserTable } from './-components/UserTable.js';
import { EditUser } from './-forms/EditUser.js';
import { NewUser } from './-forms/NewUser.js';

export const Route = createFileRoute('/_admin/admin/user')({
	component: function UserAdminPage() {
		const [drawerOpen, setDrawerOpen] = useState<'new' | 'edit' | null>(null);

		const [editUserId, setEditUserId] = useState<string | null>(null);

		const confirm = useConfirm();

		const user = useUser();

		if (!user.permissions.includes('admin.users')) {
			return (
				<Container
					maxWidth="lg"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						backgroundColor: 'offwhite.light',
						pt: 2,
						mt: 2,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							p: 4,
							gap: 1,
							position: 'relative',
							m: 'auto',
						}}
					>
						<FontAwesomeIcon
							color="darkred"
							icon={faTriangleExclamation}
							size="2xl"
						/>
						<Typography variant="subtitle1">Access Denied</Typography>
						<Typography variant="body2">
							You do not have access to this page
						</Typography>
					</Box>
				</Container>
			);
		}

		return (
			<Container
				maxWidth="xl"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					backgroundColor: 'offwhite.light',
					pt: 2,
					mt: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography variant="h4" component="h1">
						User Administration
					</Typography>
					{user.permissions.includes('admin.users.add') && (
						<Button
							color="button"
							variant="outlined"
							startIcon={<FontAwesomeIcon icon={faUserPlus} />}
							sx={(theme) => ({
								'& .MuiButton-startIcon svg': {
									fontSize: '1rem',
								},
								color: 'button.main',
								borderColor: 'green.main',
								backgroundColor: 'offwhite.light',
								'& .MuiButton-startIcon': {
									'--fa-secondary-opacity': '1',
									'--fa-secondary-color': theme.palette.green.main,
									'--fa-primary-color': theme.palette.green.dark,
								},
								'&:hover': {
									borderColor: 'green.dark',
									backgroundColor: 'green.light',
								},
							})}
							onClick={() => {
								setDrawerOpen(drawerOpen === 'new' ? null : 'new');
							}}
						>
							Add a new user
						</Button>
					)}
				</Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						borderRadius: 2,
						paddingTop: 2,
					}}
				>
					<UserTable
						handleEdit={(userId: string) => {
							setEditUserId(userId);
							setDrawerOpen('edit');
						}}
					/>
				</Box>
				<RightSlide
					open={drawerOpen === 'edit'}
					onClose={() => {}}
					width="100%"
				>
					{drawerOpen === 'edit' && editUserId && (
						<Paper
							sx={(theme) => ({
								animation: 'open 0.2s ease-in-out',
								width: '100%',
								height: '100%',
								position: 'absolute',
								right: 0,
								top: 0,
								overflowY: 'auto',
								backgroundColor: theme.palette.offwhite.light,
								zIndex: 1000,
								'@keyframes open': {
									from: {
										width: 0,
									},
									to: {
										width: '100%',
									},
								},
							})}
						>
							<Suspense fallback={<LinearProgress />}>
								<EditUser
									userId={editUserId}
									onClose={() => {
										setDrawerOpen(null);
									}}
								/>
							</Suspense>
						</Paper>
					)}
				</RightSlide>
				<RightSlide
					open={drawerOpen === 'new'}
					onClose={() => {
						// do nothing
					}}
					width="min(45vw, 650px)"
				>
					<Suspense fallback={<LinearProgress />}>
						<NewUser
							onClose={() => {
								setDrawerOpen(null);
							}}
							onSuccess={(userId: string | null) => {
								if (!userId) {
									setDrawerOpen(null);
								} else {
									void confirm({
										title: 'User Created Successfully',
										description:
											'An onboarding email has been sent to the user to configure their password',
										cancellationText: 'Close',
										confirmationText: 'View User Profile',
									}).then(({ confirmed }) => {
										if (confirmed) {
											setEditUserId(userId);
											setDrawerOpen('edit');
										} else {
											setDrawerOpen(null);
										}
									});
								}
							}}
						/>
					</Suspense>
				</RightSlide>
				<Backdrop open={drawerOpen === 'new'} />
			</Container>
		);
	},
	head: (ctx) => ctx.match.context.setTitle('Admin', 'Users'),
});
